import type { HTMLAttributes, ReactNode } from 'react';

import { SOFT_TONE_CLASS } from '../styles/tone.js';
import type { SemanticTone } from '../styles/tone.js';
import { Icon } from './Icon.js';
import type { IconName } from './Icon.js';

/** All four semantic tones, so this is `SOFT_TONE_CLASS` itself rather than a local `TONE_CLASSES` copy of it. */
export type StatusTone = SemanticTone;

export interface StatusMessageProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  children: ReactNode;
  tone?: StatusTone;
  title?: ReactNode;
}

/** A decorative glyph paired with — never replacing — the explicit wording (docs/UI_GUIDELINES.md §2, §8). */
const TONE_ICON: Record<StatusTone, IconName> = {
  danger: 'warning',
  info: 'info',
  success: 'check',
  warning: 'warning',
};

/** Shared inline feedback treatment; meaning is carried by text and role, never hue alone. */
export function StatusMessage({
  children,
  tone = 'info',
  title,
  className = '',
  ...rest
}: StatusMessageProps) {
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={`flex gap-3 rounded-lg border-l-strong p-4 shadow-1 ${SOFT_TONE_CLASS[tone]} ${className}`}
      {...rest}
    >
      <Icon name={TONE_ICON[tone]} className="mt-0.5 h-5 w-5 shrink-0" />
      <div className="min-w-0">
        {title && <p className="font-semibold">{title}</p>}
        <div className={title ? 'mt-1 text-sm' : ''}>{children}</div>
      </div>
    </div>
  );
}
